import { create } from 'zustand'

import {
  getInboundEmails,
  markEmailAsRead,
  markEmailAsUnread,
  getUnreadCount,
  type InboundEmail,
} from '../api/inboxService'

export interface InboxState {
  emails: InboundEmail[]
  unreadCount: number
  isLoading: boolean
  error: string | null
  selectedEmailId: string | null
  filter: 'all' | 'unread'
  loadEmails: () => Promise<void>
  refreshUnreadCount: () => Promise<void>
  markAsRead: (id: string) => Promise<void>
  markAsUnread: (id: string) => Promise<void>
  selectEmail: (id: string | null) => void
  setFilter: (filter: 'all' | 'unread') => void
  addEmail: (email: InboundEmail) => void
}

export const useInboxStore = create<InboxState>((set) => ({
  emails: [],
  unreadCount: 0,
  isLoading: false,
  error: null,
  selectedEmailId: null,
  filter: 'all',

  loadEmails: async () => {
    set({ isLoading: true, error: null })
    try {
      const { results } = await getInboundEmails({ pageSize: 100 })
      set({ emails: results, isLoading: false })
    } catch (err) {
      set({ error: err instanceof Error ? err.message : 'Error loading emails', isLoading: false })
    }
  },

  refreshUnreadCount: async () => {
    try {
      const count = await getUnreadCount()
      set({ unreadCount: count })
    } catch {
      // silently fail
    }
  },

  markAsRead: async (id) => {
    await markEmailAsRead(id)
    set((s) => ({
      emails: s.emails.map((e) => (e.id === id ? { ...e, isRead: true } : e)),
      unreadCount: Math.max(0, s.unreadCount - 1),
    }))
  },

  markAsUnread: async (id) => {
    await markEmailAsUnread(id)
    set((s) => ({
      emails: s.emails.map((e) => (e.id === id ? { ...e, isRead: false } : e)),
      unreadCount: s.unreadCount + 1,
    }))
  },

  selectEmail: (id) => set({ selectedEmailId: id }),
  setFilter: (filter) => set({ filter }),

  addEmail: (email) => {
    set((s) => ({
      emails: [email, ...s.emails],
      unreadCount: s.unreadCount + (email.isRead ? 0 : 1),
    }))
  },
}))
